import imageSegmentationApi from '@frontend/services/api/image-segmentation';
import {
  imageSegmentationEventBroker,
  IMAGE_SEGMENTATION_EVENTS,
} from '@frontend/group/modules/image-segmentation/event-broker';
import { normalizeBrowserUrl } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';
import { resolveImageEditSource } from '@frontend/store/creative-studio/image-edit';
import { downloadFileFromUrl } from '@frontend/utils';

function parseArgs($message) {
  try {
    const raw = $message.function_call.arguments;

    return (typeof raw === 'string' ? JSON.parse(raw) : raw) || {};
  } catch {
    return {};
  }
}

/**
 * Start the existing segmentation preview from a text command in AI Studio.
 * The user can say things like "please layer this" after attaching an image.
 */
export async function handleSplitImageIntoLayers($message, { state, dispatch } = {}) {
  const args = parseArgs($message);
  const messages = state?.messages || [];
  const lastUserMessage = [...messages]
    .reverse()
    .find(message => message && message.role === 'user');
  const source = resolveImageEditSource({
    imageIndex: Number.isInteger(args.image_index) ? args.image_index : null,
    savedAttachments: $message.savedAttachments || [],
    userAttachments: lastUserMessage?.attachments || [],
    messages,
  });

  if (!source) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'Please attach, paste, or select the image you want to split into layers.',
    });
    return;
  }

  const imagePath = normalizeBrowserUrl(source.url);
  const progressMessage = await dispatch('addAndSaveMessage', {
    role: 'assistant',
    content: 'Analyzing the image and preparing its layers…',
  });

  const reportFailure = async () => {
    if (progressMessage?.id) {
      const index = (state.messages || []).findIndex(message => message?.id === progressMessage.id);
      if (index !== -1) {
        await dispatch('updateAndSaveMessage', {
          index,
          patch: {
            content: 'I could not start image layering. Please try again.',
          },
        });
        return;
      }
    }

    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'I could not start image layering. Please try again.',
    });
  };


  const cleanupLayerListeners = () => {
    imageSegmentationEventBroker.off(
      IMAGE_SEGMENTATION_EVENTS.AI_STUDIO_LAYERS_CONFIRMED,
      onLayersConfirmed
    );
    imageSegmentationEventBroker.off(
      IMAGE_SEGMENTATION_EVENTS.AI_STUDIO_LAYERS_DOWNLOAD_REQUESTED,
      onLayersDownloadRequested
    );
    imageSegmentationEventBroker.off(IMAGE_SEGMENTATION_EVENTS.CLOSED, onLayersClosed);
  };

  const onLayersClosed = () => {
    cleanupLayerListeners();
  };

  const onLayersDownloadRequested = async ({ layers = [] } = {}) => {
    if (!layers.length) return;

    try {
      for (const [index, layer] of layers.entries()) {
        if (!layer.imageUrl) continue;
        await downloadFileFromUrl(
          layer.imageUrl,
          `${String(index + 1).padStart(2, '0')}-${layer.name || 'layer'}.png`
        );
      }
    } catch (error) {
      console.warn('Failed to download all AI Studio layers:', error);
    }
  };

  const onLayersConfirmed = async ({ layers = [] } = {}) => {
    cleanupLayerListeners();

    try {
      if (!layers.length) {
        await dispatch('addAndSaveMessage', {
          role: 'assistant',
          content: 'No layers were selected.',
        });
        return;
      }

      const attachments = layers.map((layer, index) => ({
        type: 'url',
        payload: layer.imageUrl,
        name: `${String(index + 1).padStart(2, '0')}-${layer.name}.png`,
      }));

      await dispatch('addAndSaveMessage', {
        role: 'assistant',
        content: `I separated the image into ${layers.length} layers.`,
        attachments,
        _showAllAttachments: true,
      });
    } catch (error) {
      console.warn('Failed to return image layers to AI Studio:', error);
      await dispatch('addAndSaveMessage', {
        role: 'assistant',
        content: 'The layers were created, but I could not download all of them.',
      });
    }
  };

  imageSegmentationEventBroker.on(
    IMAGE_SEGMENTATION_EVENTS.AI_STUDIO_LAYERS_CONFIRMED,
    onLayersConfirmed
  );
  imageSegmentationEventBroker.on(
    IMAGE_SEGMENTATION_EVENTS.AI_STUDIO_LAYERS_DOWNLOAD_REQUESTED,
    onLayersDownloadRequested
  );
  imageSegmentationEventBroker.on(IMAGE_SEGMENTATION_EVENTS.CLOSED, onLayersClosed);
  const customerId = 0;
  try {
    const { data } = await imageSegmentationApi.startSegmentation(
      customerId,
      imagePath,
      null,
      null,
      null,
      null,
      null,
      false
    );

    if (!data?.job_uuid) {
      await reportFailure();
      return;
    }

    imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.SHOW_PREVIEW, {
      jobUuid: data.job_uuid,
      customerId,
      imagePath,
      originalImagePath: data.original_image_path || imagePath,
      layoutId: null,
      layoutName: source.name ? `${source.name} Layers` : 'AI Studio Layers',
      instanceId: null,
      replaceTemplateInstanceId: null,
      displayedWidth: data.displayed_width || null,
      displayedHeight: data.displayed_height || null,
      aiStudioMode: true,
    });

    // The modal owns the processing state after it opens. Keep the progress message
    // as a simple audit entry in the chat for this first integration.
    return progressMessage;
  } catch (error) {
    console.warn('Image layering failed to start:', error);
    await reportFailure();
  }
}

